/* eslint-disable @typescript-eslint/no-explicit-any */
import { themeColors } from '@juvare/common-web-ui/JuvareTheme';
import { BaseWidgetEditorComponent } from '@juvare/widget-ui/BaseWidgetEditorComponent';
import AddIcon from '@mui/icons-material/Add';
import BorderAllIcon from '@mui/icons-material/BorderAll';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, Button, Collapse, Dialog, DialogActions, DialogContent, DialogTitle, Divider, FormControl, IconButton, Paper, Typography, } from '@mui/material';
import React from 'react';
import { buildEventTab, buildGeneralTab, buildHiddenTab, buildModalTab, buildStyleTab, buildValidateTab, showWidgetEditDialog, } from '../WidgetEditDialog';
import { FormHelperErrorDescription } from '../WidgetForm/Components';
import { defaultWidgetLabelProvider, getLabelAndValuePairsFromChildDynamicRows, getVisibleError, handleWidgetOnLoad, handleWidgetUpdate, isSkipValidate, joinLabels, makeNewConfigUnique, renderForm, setModalChildSkipValidate, validateCustom, } from '../WidgetForm/Utils';
import { TemplateOutput } from '../WidgetFormContent/Components';
import { EditModeDialog, ModalDialogForm } from '../WidgetFormModalPanel/Components';
import { BaseLayoutColumn } from '../WidgetFormPanel/Components';
import { WidgetHandler } from '../WidgetHandler';
import { type } from './types';
const WidgetFormModalRowList = (props) => {
    var _a, _b, _c, _d, _e, _f;
    const length = props.childDynamicRows ? props.childDynamicRows.length : 0;
    const maxLength = (_a = props.config.validate) === null || _a === void 0 ? void 0 : _a.maxLength;
    const allowAdd = maxLength == null || length < maxLength;
    const error = getVisibleError(props);
    const childDynamicRows = (_b = props.childDynamicRows) !== null && _b !== void 0 ? _b : [];
    const getModalChildProps = (index) => {
        const row = index === -1 ? childDynamicRows[childDynamicRows.length - 1] : childDynamicRows[index];
        if (props.config.row.children) {
            return row.childProps.slice(props.config.row.children.length);
        }
        else {
            return row.childProps;
        }
    };
    const getRowChildProps = (index) => {
        const row = index === -1 ? childDynamicRows[childDynamicRows.length - 1] : childDynamicRows[index];
        if (props.config.row.children) {
            return row.childProps.slice(0, props.config.row.children.length);
        }
        else {
            return [];
        }
    };
    const showModal = (index) => {
        if (index === -1) {
            props.internalData.modalState = { open: true, index, data: {} };
        }
        else {
            const data = props.getDataArray()[index];
            props.internalData.modalState = { open: true, index, data: data != null ? JSON.parse(JSON.stringify(data)) : {} };
        }
        props.update();
        renderForm(props);
    };
    const onSave = (data) => {
        const index = props.internalData.modalState.index;
        if (index === -1) {
            props.insertDataRow(-1);
            const insertedRowChildProps = childDynamicRows[childDynamicRows.length - 1].childProps;
            if (props.config.row.children) {
                setModalChildSkipValidate(insertedRowChildProps.slice(props.config.row.children.length));
            }
            else {
                setModalChildSkipValidate(insertedRowChildProps);
            }
            const array = props.getDataArray();
            array[array.length - 1] = data;
            props.setData(array);
        }
        else {
            const array = props.getDataArray();
            array[index] = data;
            props.setData(array);
        }
        props.internalData.modalState.open = false;
        renderForm(props, { changed: true, autoSaveImmediately: props.config.autoSave });
    };
    const onCancel = () => {
        props.internalData.modalState.open = false;
        props.update();
        renderForm(props);
    };
    const onCloseAfterTransition = () => {
        delete props.internalData.modalState;
        props.update();
        renderForm(props);
    };
    const toggleRow = (index) => {
        if (!props.internalData.expandedRows) {
            props.internalData.expandedRows = [];
        }
        props.internalData.expandedRows[index] = !props.internalData.expandedRows[index];
        props.update();
        renderForm(props);
    };
    const deleteRow = (index) => {
        props.internalData.deleteRowIndex = index;
        props.update();
        renderForm(props);
    };
    const onCancelDelete = () => {
        delete props.internalData.deleteRowIndex;
        props.update();
        renderForm(props);
    };
    const onConfirmDelete = () => {
        const index = props.internalData.deleteRowIndex;
        delete props.internalData.deleteRowIndex;
        props.deleteDataRow(index);
        if (props.internalData.expandedRows) {
            props.internalData.expandedRows.splice(index, 1);
        }
        renderForm(props, { changed: true, autoSaveImmediately: props.config.autoSave });
    };
    const headerSx = {
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: { sm: 'center' },
        gap: 2,
        p: { xs: 2, sm: 0 },
        pl: { sm: 2 },
        pr: { sm: 1.5 },
    };
    const extensionActions = ((_c = props.config.extension) === null || _c === void 0 ? void 0 : _c.getActions) ? props.config.extension.getActions(new WidgetHandler(props)) : [];
    return (React.createElement(FormControl, { fullWidth: true, variant: 'outlined', error: !!error },
        React.createElement(Paper, { variant: 'outlined' },
            React.createElement(Box, { sx: headerSx },
                React.createElement(Typography, { variant: 'subtitle1', sx: { flexGrow: { sm: 1 }, pt: { sm: 2 }, pb: { sm: 2 } } }, props.config.label),
                allowAdd && (React.createElement(Button, { startIcon: React.createElement(AddIcon, null), variant: 'text', onClick: () => showModal(-1), tabIndex: props.editMode ? -1 : undefined, "data-test": 'add-button', autoFocus: (_d = (!props.editMode && props.config.autoFocus)) !== null && _d !== void 0 ? _d : false }, (_e = props.config.addButtonLabel) !== null && _e !== void 0 ? _e : 'Add')),
                extensionActions.length > 0 && (React.createElement(React.Fragment, null,
                    allowAdd && (React.createElement(React.Fragment, null,
                        React.createElement(Divider, { flexItem: true, orientation: 'vertical', sx: { display: { xs: 'none', sm: 'block', height: 32, alignSelf: 'center' } } }),
                        React.createElement(Divider, { flexItem: true, orientation: 'horizontal', sx: { display: { xs: 'block', sm: 'none' } } }))),
                    extensionActions.map((action) => (React.createElement(Button, { key: action.label, sx: { whiteSpace: 'nowrap' }, variant: 'outlined', startIcon: action.icon, onClick: () => {
                            action.onClick(new WidgetHandler(props));
                        } }, action.label)))))),
            props.editMode ? (React.createElement(React.Fragment, null,
                React.createElement(EditModeDialog, { config: props.config },
                    React.createElement(BaseLayoutColumn, { spacing: 16, childrenEditorPath: 'modal.children', childProps: getModalChildProps(0), renderData: props.renderData, editMode: props.editMode, dragTargetPlaceholderMessage: 'Drop here' })),
                React.createElement(Box, { sx: { backgroundColor: themeColors.surface.secondary } },
                    React.createElement(Box, { sx: { display: 'flex', alignItems: 'center' } },
                        React.createElement(Box, { p: 2, pr: 0 },
                            React.createElement(ExpandMoreIcon, { sx: { p: 1 } })),
                        React.createElement(Box, { sx: { p: 2, pr: 0, flexGrow: 1, alignSelf: 'center' } }, props.config.row.template && props.childDynamicRows && (React.createElement(Typography, { component: 'div' },
                            React.createElement(TemplateOutput, { template: props.config.row.template, props: props, row: props.childDynamicRows[0] })))),
                        React.createElement(Box, { p: 2 },
                            React.createElement(DriveFileRenameOutlineIcon, { sx: { p: 1 } }),
                            React.createElement(DeleteOutlineIcon, { sx: { p: 1 } }))),
                    props.config.row.children && (React.createElement(Box, { p: 2, pt: 0 },
                        React.createElement(BaseLayoutColumn, { spacing: 16, childrenEditorPath: 'row.children', childProps: getRowChildProps(0), renderData: props.renderData, editMode: props.editMode })))))) : (React.createElement(React.Fragment, null,
                ((_f = props.internalData.modalState) === null || _f === void 0 ? void 0 : _f.index) === -1 && (React.createElement(ModalDialogForm, { open: props.internalData.modalState.open, data: props.internalData.modalState.data, parentModalWidgetHandler: new WidgetHandler(props), formParams: props.rootParams.formParams, valueProviders: props.rootParams.valueProviders, subFormProviders: props.rootParams.subFormProviders, globalWidgetConfig: props.rootParams.globalWidgetConfig, rules: props.rootParams.rules, widgets: props.widgets, onSave: onSave, onCancel: onCancel, onCloseAfterTransition: onCloseAfterTransition, config: props.config })),
                childDynamicRows.length > 0 && (React.createElement(Box, { p: 2 }, childDynamicRows.map((row, index) => {
                    var _a, _b, _c;
                    return (React.createElement(React.Fragment, { key: row.id },
                        React.createElement(Box, { sx: { mt: index > 0 ? 2 : 0, backgroundColor: themeColors.surface.secondary } },
                            React.createElement(Box, { sx: { display: 'flex', alignItems: 'center' }, "data-test": 'row-header' },
                                props.config.row.children != null && (React.createElement(Box, { p: 2, pr: 0, onMouseDown: () => toggleRow(index) },
                                    React.createElement(IconButton, { color: 'primary', sx: {
                                            transition: (theme) => theme.transitions.create(['transform']),
                                            transform: !((_a = props.internalData.expandedRows) === null || _a === void 0 ? void 0 : _a[index])
                                                ? 'rotate(0deg)'
                                                : 'rotate(-180deg)',
                                        }, onMouseDown: (event) => event.stopPropagation(), onClick: () => toggleRow(index), "aria-label": 'Toggle ' + props.config.label + ' Row ' + (index + 1), tabIndex: props.editMode ? -1 : undefined, "data-test": 'toggle-button' },
                                        React.createElement(ExpandMoreIcon, null)))),
                                React.createElement(Box, { sx: { p: 2, pr: 0, flexGrow: 1, alignSelf: 'center' }, onMouseDown: () => toggleRow(index) },
                                    props.config.row.template && (React.createElement(Typography, { component: 'div', "data-test": 'template' },
                                        React.createElement(TemplateOutput, { template: props.config.row.template, props: props, row: row }))),
                                    props.config.row.render && props.config.row.render(new WidgetHandler(props, row))),
                                React.createElement(Box, { p: 2 },
                                    React.createElement(IconButton, { color: 'primary', onClick: () => showModal(index), "aria-label": 'Edit ' + props.config.label + ' Row ' + (index + 1), tabIndex: props.editMode ? -1 : undefined, "data-test": 'edit-button' },
                                        React.createElement(DriveFileRenameOutlineIcon, null)),
                                    React.createElement(IconButton, { color: 'primary', onClick: () => deleteRow(index), "aria-label": 'Delete ' + props.config.label + ' Row ' + (index + 1), tabIndex: props.editMode ? -1 : undefined, "data-test": 'delete-button' },
                                        React.createElement(DeleteOutlineIcon, null)),
                                    React.createElement(Dialog, { open: props.internalData.deleteRowIndex === index, onClose: (event, reason) => {
                                            if (reason === 'escapeKeyDown') {
                                                onCancelDelete();
                                            }
                                        } },
                                        React.createElement(DialogTitle, null, "Are you sure you want to delete this row?"),
                                        React.createElement(DialogContent, null,
                                            React.createElement(Typography, null, "By deleting, you will remove all the information you\u2019ve entered.")),
                                        React.createElement(DialogActions, null,
                                            React.createElement(Button, { color: 'primary', onClick: onCancelDelete, "data-test": 'cancel-button' }, "Cancel"),
                                            React.createElement(Button, { color: 'primary', onClick: onConfirmDelete, "data-test": 'delete-button' }, "Delete"))))),
                            ((_b = props.internalData.modalState) === null || _b === void 0 ? void 0 : _b.index) === index && (React.createElement(ModalDialogForm, { open: props.internalData.modalState.open, data: props.internalData.modalState.data, parentModalWidgetHandler: new WidgetHandler(props, row), formParams: props.rootParams.formParams, valueProviders: props.rootParams.valueProviders, subFormProviders: props.rootParams.subFormProviders, globalWidgetConfig: props.rootParams.globalWidgetConfig, rules: props.rootParams.rules, widgets: props.widgets, onSave: onSave, onCancel: onCancel, onCloseAfterTransition: onCloseAfterTransition, config: props.config })),
                            props.config.row.children != null && (React.createElement(Collapse, { in: !!((_c = props.internalData.expandedRows) === null || _c === void 0 ? void 0 : _c[index]) },
                                React.createElement(Box, { p: 2, pt: 0, "data-test": 'row-body' },
                                    React.createElement(BaseLayoutColumn, { spacing: 16, childrenEditorPath: 'children', childProps: getRowChildProps(index), renderData: props.renderData, editMode: props.editMode })))))));
                }))))),
            React.createElement(FormHelperErrorDescription, { error: error }))));
};
const WidgetFormModalRowListEditor = (props) => {
    return (React.createElement(BaseWidgetEditorComponent, Object.assign({}, props, { widgetLabelProvider: defaultWidgetLabelProvider, onEditWidget: (endEditing) => showWidgetEditDialog(props, endEditing, { title: 'Modal List: ' + props.config.label }, buildGeneralTab(props, {
            key: true,
            label: true,
            autoFocus: true,
            children: [
                {
                    type: 'form.text',
                    key: 'addButtonLabel',
                    label: 'Add Button Label',
                    description: 'Default: Add',
                    emptyAsNull: true,
                },
                {
                    type: 'form.template',
                    key: 'row.template',
                    label: 'Template',
                    props: props,
                },
                {
                    type: 'form.checkbox',
                    key: 'rowsHaveChildren',
                    label: 'Rows Have Children',
                },
            ],
            onLoad: (data) => {
                var _a;
                data.rowsHaveChildren = ((_a = data.row) === null || _a === void 0 ? void 0 : _a.children) != null;
                return data;
            },
            onSubmit: (data) => {
                var _a, _b;
                if (data.rowsHaveChildren) {
                    if (!((_a = data.row) === null || _a === void 0 ? void 0 : _a.children)) {
                        if (!data.row) {
                            data.row = {};
                        }
                        data.row.children = [];
                    }
                }
                else {
                    if ((_b = data.row) === null || _b === void 0 ? void 0 : _b.children) {
                        delete data.row.children;
                    }
                }
                delete data.rowsHaveChildren;
                return data;
            },
        }), buildValidateTab(props, {
            required: true,
            maxLength: true,
        }), buildModalTab(props), buildHiddenTab(props), buildStyleTab(props), buildEventTab(props, { eventOnLoad: true, eventOnChange: true, autoSave: true })) }),
        React.createElement(WidgetFormModalRowList, Object.assign({}, props))));
};
export const widgetFormModalRowList = {
    type,
    init: (props) => {
        if (props.childDynamicRows) {
            for (const row of props.childDynamicRows) {
                if (props.config.row.children) {
                    setModalChildSkipValidate(row.childProps.slice(props.config.row.children.length));
                }
                else {
                    setModalChildSkipValidate(row.childProps);
                }
            }
        }
    },
    load: (props) => {
        handleWidgetOnLoad(props, props.getData());
    },
    validate: (props) => {
        var _a, _b;
        if (isSkipValidate(props)) {
            return;
        }
        const values = props.getDataArray();
        if (props.config.validate) {
            if (props.config.validate.required) {
                if (values.length === 0) {
                    return (_a = props.config.validate.requiredMessage) !== null && _a !== void 0 ? _a : (props.config.label ? props.config.label + ' is required' : 'Required');
                }
            }
            if (props.config.validate.maxLength) {
                if (values.length > props.config.validate.maxLength) {
                    return ((_b = props.config.validate.maxLengthMessage) !== null && _b !== void 0 ? _b : (props.config.label ? props.config.label + ' length cannot be greater than ' : 'Length cannot be greater than ') +
                        props.config.validate.maxLength);
                }
            }
        }
        return validateCustom(props, values);
    },
    update: (props) => {
        return handleWidgetUpdate(props);
    },
    getLabelValueAsTextPairs: (props, labelPrefix) => {
        return getLabelAndValuePairsFromChildDynamicRows(props, joinLabels(labelPrefix, props.config.label));
    },
    childConfigs: (config) => {
        if (config.row.children) {
            return [...config.row.children, ...config.modal.children];
        }
        else {
            return config.modal.children;
        }
    },
    childDynamicRows: true,
    component: WidgetFormModalRowList,
    icon: React.createElement(BorderAllIcon, { fontSize: 'small' }),
    editorComponent: WidgetFormModalRowListEditor,
    createWidget: (rootConfig, editorPath, widgets) => makeNewConfigUnique(rootConfig, editorPath, widgets, {
        type,
        key: 'modalRowList',
        label: 'Modal Row List',
        modal: { maxWidth: 'xs', fullWidth: true, children: [] },
        row: {},
    }),
};
